/**
 * /operations/transactions/eos/new — EOS HR-initiate placeholder
 * (M30.5.1, per Amer's M30.5 refinement #2).
 *
 * EOS is deliberately deferred to M30.6 because it deserves a
 * focused sprint: Article 80 forfeiture checklist, beneficiary
 * identification for deceased employees, government filings, and a
 * downstream-effect confirmation modal that lists every side
 * effect (final settlement amount, dependent count, last working
 * day, Mudad notification flag, GOSI termination flag).
 *
 * The route + placeholder card EXIST today so the Hub's EOS card
 * click-through doesn't 404. The page presents:
 *   - The employee picker (so HR knows which employee they were
 *     about to terminate — useful when they come back to do it
 *     in M30.6)
 *   - A clearly-labeled "Coming M30.6" card with the sponsor
 *     message
 *   - Two escape hatches: a link to the existing /operations/eos
 *     workflow (M18 legacy) + a contact-support link
 *
 * No commit path. The backend EOS HR-initiate endpoint exists
 * (M30 P6 shipped), so anyone scripting against the API can still
 * post EOS transactions; the count + aggregator math both work.
 */

import { Info } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { EmployeePicker } from "../../components/ui/EmployeePicker";
import type { EmployeePickerOption } from "../../lib/api";
import { PageHeader } from "../../components/ui";

export function EosPlaceholderPage() {
  const { t } = useTranslation();
  const [employee, setEmployee] = useState<EmployeePickerOption | null>(
    null,
  );

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <PageHeader
        title={t("operations.eos_placeholder_title")}
        subtitle={t("operations.eos_placeholder_subtitle")}
      />

      <div className="rounded-lg border border-border-subtle bg-surface-raised p-4 shadow-sm">
        <EmployeePicker
          value={employee?.id ?? null}
          onChange={setEmployee}
          selected={employee}
        />
      </div>

      <section className="flex gap-3 rounded-lg border border-state-paused/30 bg-state-paused-bg/40 p-4">
        <Info
          aria-hidden
          className="h-5 w-5 shrink-0 text-state-paused"
        />
        <div className="flex flex-col gap-2">
          <h2 className="text-base font-semibold text-text-primary">
            {t("operations.eos_placeholder_headline")}
          </h2>
          <p className="text-sm text-text-secondary">
            {t("operations.eos_placeholder_body")}
          </p>
          <ul className="ms-4 list-disc text-sm text-text-secondary">
            <li>
              <Link
                to="/operations/eos"
                className="text-brand underline hover:no-underline"
              >
                {t("operations.eos_placeholder_existing_link")}
              </Link>
            </li>
            <li>
              <a
                href="mailto:support@solvait.ai"
                className="text-brand underline hover:no-underline"
              >
                {t("operations.eos_placeholder_urgent_link")}
              </a>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}
